import { useState } from 'react';
import { toast } from 'sonner';
import { userApi } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Card, CardBody, CardHeader, Button, Input, Badge } from '../../components/ui';
import { Gift, Users, Copy, Check, CheckCircle2, Clock, Coins } from 'lucide-react';

export function ReferralPage() {
  const { user } = useAuthStore();
  const [copied, setCopied] = useState(false);

  const { data: profileData } = useApiQuery({
    queryKey: ['profile'],
    queryFn: () => userApi.getProfile(),
    refetchOnMount: 'always',
  });

  const { data: referralsData } = useApiQuery({
    queryKey: ['referrals'],
    queryFn: () => userApi.getReferrals(),
    refetchOnMount: 'always',
  });

  const profile = profileData || user;
  const referralLink = profile?.referral_code
    ? `${window.location.origin}/register?ref=${profile.referral_code}`
    : null;

  const handleCopyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const total = referralsData?.total ?? 0;
  const completed = referralsData?.completed ?? 0;
  const totalCreditsEarned = referralsData?.total_credits_earned ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Gift className="w-8 h-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Referral Program</h1>
          <p className="text-gray-500">Invite friends and earn credits when they complete tasks</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardBody className="!p-0">
            <div className="relative overflow-hidden flex items-center justify-end">
              <Users className="absolute -left-2 top-1/2 -translate-y-1/2 w-16 h-16 text-purple-100" />
              <div className="relative px-4 py-3 text-right">
                <p className="text-xs font-semibold text-gray-500">Total Invited</p>
                <p className="text-3xl font-bold text-purple-600">{total}</p>
              </div>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="!p-0">
            <div className="relative overflow-hidden flex items-center justify-end">
              <CheckCircle2 className="absolute -left-2 top-1/2 -translate-y-1/2 w-16 h-16 text-green-100" />
              <div className="relative px-4 py-3 text-right">
                <p className="text-xs font-semibold text-gray-500">Completed</p>
                <p className="text-3xl font-bold text-green-600">{completed}</p>
              </div>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="!p-0">
            <div className="relative overflow-hidden flex items-center justify-end">
              <Coins className="absolute -left-2 top-1/2 -translate-y-1/2 w-16 h-16 text-yellow-100" />
              <div className="relative px-4 py-3 text-right">
                <p className="text-xs font-semibold text-gray-500">Credits Earned</p>
                <p className="text-3xl font-bold text-yellow-600">{totalCreditsEarned}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Referral Link */}
      <Card>
        <CardHeader>
          <h2 className="font-semibold text-gray-900">Your Referral Link</h2>
          <p className="text-sm text-gray-500 mt-1">
            Share this link with friends. You earn a percentage of their task credits when they join and complete tasks.
          </p>
        </CardHeader>
        <CardBody>
          {referralLink ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={referralLink}
                  readOnly
                  className="font-mono text-xs bg-gray-50"
                />
                <Button variant="secondary" onClick={handleCopyLink} title="Copy link">
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-400">
                Your referral code: <span className="font-mono font-semibold text-gray-600">{profile?.referral_code}</span>
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No referral code available.</p>
          )}
        </CardBody>
      </Card>

      {/* Invited People */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              People You Invited
            </h2>
            {total > 0 && (
              <span className="text-sm text-gray-500">{completed}/{total} completed</span>
            )}
          </div>
        </CardHeader>
        <CardBody className="!p-0">
          {!referralsData || referralsData.referrals.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Gift className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No referrals yet. Share your link to get started!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Credits Earned</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {referralsData.referrals.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{r.full_name}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {new Date(r.joined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.first_task_approved ? (
                          <Badge variant="green">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                        ) : (
                          <Badge variant="yellow">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-yellow-600">
                        {r.credits_earned > 0 ? r.credits_earned : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
